import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { useTheresiasEventBridge } from "./useTheresiasEventBridge";
import { getStreamingActiveMirror } from "../../utils/streamingState";

/**
 * Dead-click telemetry. Mount once at App root.
 *
 * Fires ONE Theresias event per dead-click cluster:
 *   browser-native-dead-click
 *
 * A "dead click" is a click on an interactive-looking element that yields no
 * user-perceivable response within `DETECTION_WINDOW_MS`. The full strategy is
 * spec'd in Appendix D of the Theresias proposal — pseudocode reproduced here:
 *
 *   1. Capture-phase click listener at document (fires BEFORE app handlers).
 *   2. Eligibility filter — interactive-looking, not excluded, under caps.
 *   3. Snapshot pre-click state (URL, screen, focus, scroll, perf baseline).
 *   4. Arm MutationObserver (DOM changes) + PerformanceObserver (network).
 *   5. Wait 800 ms verdict.
 *   6. Evaluate 6 response checks (navigation, screen, DOM, network, focus,
 *      media/scroll). ANY passing check defuses the event silently.
 *   7. Noise control — per-view dedupe, 1.5 s trailing rage debounce, session
 *      cap 20.
 *   8. Fire the event via the same Theresias pipeline used by the other
 *      twelve events. `chatId`, `itineraryIndex`, and `ownerEmail` are
 *      auto-injected by the bridge.
 *
 * Coexists with existing global listeners (visibilitychange, pagehide, scroll)
 * — those operate on different event types and never touch click. No app-level
 * click listener was registered at document or window before this hook, so the
 * capture-phase guarantees no conflicts.
 *
 * Zero impact on user flows: everything is passive listeners and observers.
 * Even if the tracking code throws internally, no user-facing feature breaks
 * (try/catch wraps the entire click handler).
 */

// ──── Tunable module constants ───────────────────────────────────────────
const DETECTION_WINDOW_MS = 800;
const RAGE_DEBOUNCE_MS = 1500;
const SESSION_CAP_MAX = 20;
const SESSION_CAP_KEY = "_zenvoya_theresias_dead_click_count";
const SCROLL_THRESHOLD_PX = 8;
const SELECTOR_MAX_LEN = 200;
const ELEMENT_TEXT_MAX_LEN = 80;
const INTERACTIVE_ANCESTOR_LIMIT = 3;

type Role = "button" | "link" | "pointer-styled" | "icon";
type Check =
  | "navigation"
  | "screen_change"
  | "dom_mutation"
  | "network"
  | "focus_change"
  | "media_scroll";
type Confidence = "high" | "medium";

// ──── Pure DOM helpers (module-scope; unit-testable in isolation) ────────

function readScrollY(): number {
  // Body-scroller gotcha (CLAUDE.md §"Scroll container gotcha"): on iOS Safari
  // mobile chat document.body is the active scroller, NOT window/docElement.
  // Take max of all three to cover desktop window-scrollers and iOS body-
  // scrollers in one read.
  return Math.max(
    document.body?.scrollTop ?? 0,
    document.documentElement?.scrollTop ?? 0,
    typeof window.scrollY === "number" ? window.scrollY : 0
  );
}

function getClassString(el: Element): string {
  // HTMLElement.className is a string; SVGElement.className is a
  // SVGAnimatedString with a .baseVal. Unify to a plain string.
  const cn = (el as HTMLElement).className as unknown;
  if (typeof cn === "string") return cn;
  if (cn && typeof (cn as { baseVal?: string }).baseVal === "string") {
    return (cn as { baseVal: string }).baseVal;
  }
  return "";
}

const INTERACTIVE_TAGS = new Set(["A", "BUTTON", "SELECT", "LABEL"]);
const INTERACTIVE_INPUT_TYPES = new Set(["button", "submit"]);
const INTERACTIVE_ROLES = new Set(["button", "link", "tab", "menuitem"]);
// App conventions — module-hashed names like `styles_btn__abc123` and MUI
// base button classes all match. Broad on purpose to catch the long tail of
// custom CTA components without per-component wiring.
const APP_INTERACTIVE_CLASS_PATTERNS: RegExp[] = [
  /\bMuiButtonBase-root\b/,
  /(^|_|-|\s)(btn|button|card|cta|chip|pill|appBtn|appButton)([_-]|\s|$)/i,
];

function isInteractiveElement(el: Element): boolean {
  if (INTERACTIVE_TAGS.has(el.tagName)) return true;
  if (el.tagName === "INPUT") {
    const type = (el as HTMLInputElement).type;
    if (INTERACTIVE_INPUT_TYPES.has(type)) return true;
  }
  const role = el.getAttribute("role");
  if (role && INTERACTIVE_ROLES.has(role)) return true;
  const tabIndex = el.getAttribute("tabindex");
  if (tabIndex !== null) {
    const ti = parseInt(tabIndex, 10);
    if (!Number.isNaN(ti) && ti >= 0) return true;
  }
  if (el.hasAttribute("onclick")) return true;
  const classStr = getClassString(el);
  if (classStr) {
    for (const re of APP_INTERACTIVE_CLASS_PATTERNS) {
      if (re.test(classStr)) return true;
    }
  }
  // Style-based fallback — `cursor: pointer` covers handler-only divs that
  // skip every other signal above. Most expensive check, so run last.
  try {
    if (window.getComputedStyle(el).cursor === "pointer") return true;
  } catch {
    // getComputedStyle can throw on detached/pseudo elements; ignore.
  }
  return false;
}

function findInteractiveAncestor(start: Element | null): Element | null {
  let cur: Element | null = start;
  let depth = 0;
  while (cur && depth <= INTERACTIVE_ANCESTOR_LIMIT) {
    if (isInteractiveElement(cur)) return cur;
    cur = cur.parentElement;
    depth++;
  }
  return null;
}

function isExcludedElement(start: Element): boolean {
  // Walk the same 3-ancestor window as the interactivity check. Any disabled
  // marker on the chain disqualifies — a user clicking a `<button disabled>`
  // whose interactive wrapper isn't disabled is still a no-op by design.
  let cur: Element | null = start;
  let depth = 0;
  while (cur && depth <= INTERACTIVE_ANCESTOR_LIMIT) {
    if (cur.hasAttribute("disabled")) return true;
    if (cur.getAttribute("aria-disabled") === "true") return true;
    const classStr = getClassString(cur);
    if (classStr && /\bMui-disabled\b/.test(classStr)) return true;
    cur = cur.parentElement;
    depth++;
  }
  return false;
}

function getElementRole(el: Element): Role {
  const tag = el.tagName;
  const role = el.getAttribute("role");
  if (tag === "A" || role === "link") return "link";
  if (
    tag === "BUTTON" ||
    tag === "INPUT" ||
    role === "button" ||
    role === "tab" ||
    role === "menuitem"
  ) {
    return "button";
  }
  const text = (el.textContent || "").trim();
  if (!text && el.querySelector("svg, img")) return "icon";
  return "pointer-styled";
}

function getElementText(el: Element): string | undefined {
  const raw = (el.textContent || "").trim().replace(/\s+/g, " ");
  if (!raw) return undefined;
  return raw.length > ELEMENT_TEXT_MAX_LEN
    ? raw.slice(0, ELEMENT_TEXT_MAX_LEN - 1) + "…"
    : raw;
}

function tokenizeClass(cn: string): string {
  if (!cn) return "";
  const parts = cn.split(/\s+/).filter(Boolean);
  const stable = parts.filter((c) => {
    // Strip digit-only chunks and emotion-generated `css-<hash>` classes.
    // Module-hashed names like `styles_btn__abc123` survive — they're
    // build-stable and consistent across users.
    if (/^[0-9]+$/.test(c)) return false;
    if (/^css-[a-z0-9]+$/i.test(c)) return false;
    return true;
  });
  return stable
    .slice(0, 2)
    .map((c) => {
      try {
        return `.${CSS.escape(c)}`;
      } catch {
        return `.${c.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
      }
    })
    .join("");
}

function buildElementToken(el: Element): string {
  const tag = el.tagName.toLowerCase();
  let id = "";
  if (el.id && !/^\d+$/.test(el.id)) {
    try {
      id = `#${CSS.escape(el.id)}`;
    } catch {
      // CSS.escape may not exist on very old browsers; fall back to raw id
      // with non-identifier chars stripped. Safe — used only for grouping.
      id = `#${el.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
    }
  }
  return tag + id + tokenizeClass(getClassString(el));
}

function sanitizeSelector(interactiveEl: Element): string {
  const chain: string[] = [];
  let cur: Element | null = interactiveEl;
  let depth = 0;
  while (cur && depth < INTERACTIVE_ANCESTOR_LIMIT) {
    chain.unshift(buildElementToken(cur));
    cur = cur.parentElement;
    depth++;
  }
  let selector = chain.join(" > ");
  if (selector.length > SELECTOR_MAX_LEN) {
    // Keep the trailing (innermost) portion — that's the clicked element
    // itself, the most stable grouping signal.
    selector = selector.slice(-SELECTOR_MAX_LEN);
  }
  return selector || "unknown";
}

// ──── Session cap helpers ────────────────────────────────────────────────

function readSessionCap(): number {
  try {
    return parseInt(sessionStorage.getItem(SESSION_CAP_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function bumpSessionCap(): void {
  try {
    sessionStorage.setItem(SESSION_CAP_KEY, String(readSessionCap() + 1));
  } catch {
    // sessionStorage blocked (private mode / quota) — cap is best-effort.
  }
}

// ──── Streaming flag ───────────────────

function isStreamingActive(): boolean {
  return getStreamingActiveMirror();
}

// ──── Media play-state snapshot (compared at verdict) ────────────────────

function readMediaState(): string {
  try {
    const els = document.querySelectorAll("video, audio");
    if (els.length === 0) return "";
    const parts: string[] = [];
    els.forEach((m) => {
      const media = m as HTMLMediaElement;
      parts.push(`${media.paused ? "p" : "P"}:${Math.floor(media.currentTime)}`);
    });
    return parts.join("|");
  } catch {
    return "";
  }
}

// ──── Focus-change check (with the "click moved focus to itself" carve-out) ──

const FOCUSABLE_INPUT_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

function focusChangedMeaningfully(
  snapshotFocus: Element | null,
  currentFocus: Element | null
): boolean {
  if (currentFocus === snapshotFocus) return false;
  if (!currentFocus) return false;
  // Browsers move focus to the clicked button/anchor automatically — that's
  // not a user-perceivable "response", so don't count it as focus_change.
  // Only flag genuine programmatic focus to a form field / contenteditable.
  if (FOCUSABLE_INPUT_TAGS.has(currentFocus.tagName)) return true;
  if ((currentFocus as HTMLElement).isContentEditable) return true;
  return false;
}

// ──── Per-key verdict state ──────────────────────────────────────────────

type PerViewEntry = {
  count: number;
  state: "pending" | "confirmed-dead" | "emitted";
  // Snapshot taken at first click — used to compute checks at verdict time
  // and as the basis for the emitted payload.
  selector: string;
  elementRole: Role;
  elementText?: string;
  x: number;
  y: number;
  route: string;
  viewKey: string;
  url: string;
  screenAtClick: string;
  focusAtClick: Element | null;
  scrollAtClick: number;
  mediaStateAtClick: string;
  perfStart: number;
  perfBefore: number; // number of resource entries already in the buffer at click
  // Observers / timers
  mutationObs: MutationObserver | null;
  verdictTimer: number | null;
  rageTimer: number | null;
  checksFailed: Check[];
  confidence: Confidence;
};

// ──── Hook implementation ────────────────────────────────────────────────

export function useDeadClickTracking(): void {
  const fireTheresiasEvent = useTheresiasEventBridge();
  const fireRef = useRef(fireTheresiasEvent);
  fireRef.current = fireTheresiasEvent;

  const state = useContext(StateContext);
  const {
    screen,
    activeBreadCrumbs,
    loginCreateAccountState,
    openLoaderOverlay,
    sharedCartLoading,
  }: iGlobalContext = state;

  // Mirror every render-bound value into refs so the vanilla DOM listener
  // (mounted once with empty deps) always reads the latest value without
  // re-mounting or re-rendering on every state change.
  const screenRef = useRef(screen);
  screenRef.current = screen;
  // activeBreadCrumbs is read for the screen_change verdict check but is NOT
  // part of the viewKey — spec (§7) defines viewKey = pathname + #screen only.
  // Including breadcrumb here would over-segment dedupe so the same broken
  // button would emit a separate event on each booking step.
  void activeBreadCrumbs;
  const isBusyRef = useRef(false);
  isBusyRef.current =
    !!loginCreateAccountState?.flag ||
    !!openLoaderOverlay ||
    !!sharedCartLoading;

  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // Per-view dedupe map. Keyed by `${selector}|${viewKey}`. Cleared on view
  // change so a fresh route starts with a clean slate (mirrors §7 scroll-depth
  // semantics).
  const perViewMapRef = useRef<Map<string, PerViewEntry>>(new Map());
  const currentViewKeyRef = useRef("");

  // Compute viewKey = pathname + "#" + screen (per spec §7). Two components
  // because the desktop in-route screen (`/` with screen=newBooking vs
  // screen=topDestination) is a distinct page-view, but the booking sub-step
  // breadcrumb (Flights → Hotels → Review) is NOT — a broken button at
  // /booking?breadcrumb=Flights and /booking?breadcrumb=Hotels emits ONE
  // event per view, not one per step.
  function computeViewKey(): string {
    return `${pathnameRef.current}#${screenRef.current || ""}`;
  }

  // Clear the dedupe map whenever the viewKey changes. Runs in render so the
  // first click after a view change reads the fresh empty map.
  const newKey = computeViewKey();
  if (currentViewKeyRef.current !== newKey) {
    // Disconnect any in-flight observers from the old view — they would never
    // fire a meaningful verdict on the new view anyway.
    perViewMapRef.current.forEach((entry) => {
      entry.mutationObs?.disconnect();
      if (entry.verdictTimer !== null) window.clearTimeout(entry.verdictTimer);
      if (entry.rageTimer !== null) window.clearTimeout(entry.rageTimer);
    });
    perViewMapRef.current.clear();
    currentViewKeyRef.current = newKey;
  }

  useEffect(() => {
    const map = perViewMapRef.current;

    function disconnectEntryObservers(entry: PerViewEntry): void {
      try {
        entry.mutationObs?.disconnect();
      } catch {
        // safe — disconnect on already-stopped observer is fine
      }
      if (entry.verdictTimer !== null) {
        window.clearTimeout(entry.verdictTimer);
        entry.verdictTimer = null;
      }
    }

    function emitEntry(key: string, entry: PerViewEntry): void {
      if (entry.state === "emitted") return;
      if (readSessionCap() >= SESSION_CAP_MAX) {
        entry.state = "emitted";
        return;
      }
      entry.state = "emitted";
      const payload: Record<string, unknown> = {
        selector: entry.selector,
        elementRole: entry.elementRole,
        x: entry.x,
        y: entry.y,
        route: entry.route,
        viewKey: entry.viewKey,
        clickCount: entry.count,
        checksFailed: entry.checksFailed,
        confidence: entry.confidence,
      };
      if (entry.elementText) payload.elementText = entry.elementText;
      bumpSessionCap();
      try {
        fireRef.current("browser-native-dead-click", payload);
      } catch {
        // Never let an analytics failure surface to the user.
      }
      // Keep entry in the map (state="emitted") so subsequent clicks on the
      // same key in the same view are silently ignored. Map is cleared on
      // view change.
    }

    function scheduleRageFlush(key: string, entry: PerViewEntry): void {
      if (entry.rageTimer !== null) window.clearTimeout(entry.rageTimer);
      entry.rageTimer = window.setTimeout(() => {
        entry.rageTimer = null;
        emitEntry(key, entry);
      }, RAGE_DEBOUNCE_MS);
    }

    function runVerdict(key: string, entry: PerViewEntry): void {
      const failed: Check[] = [];
      let subPerceptual = false;

      // 1. navigation
      if (window.location.href === entry.url) {
        failed.push("navigation");
      }
      // 2. screen change
      if ((screenRef.current || "") === entry.screenAtClick) {
        failed.push("screen_change");
      }
      // 3. DOM mutation — drain MutationObserver records via takeRecords.
      // Per spec §6.2: any mutation type (childList / attributes /
      // characterData) defuses the check. Text-only updates (counters,
      // label flips, streaming content) are user-perceivable responses.
      let mutationRecordCount = 0;
      let visibleMutation = false;
      try {
        const records = entry.mutationObs?.takeRecords() || [];
        mutationRecordCount = records.length;
        for (const r of records) {
          if (r.type === "childList" && (r.addedNodes.length || r.removedNodes.length)) {
            visibleMutation = true;
            break;
          }
          if (r.type === "attributes") {
            const a = r.attributeName || "";
            if (
              a === "class" ||
              a === "style" ||
              a === "hidden" ||
              a === "open" ||
              a === "value" ||
              a === "checked" ||
              a === "selected" ||
              a.startsWith("aria-") ||
              a.startsWith("data-")
            ) {
              visibleMutation = true;
              break;
            }
          }
          if (r.type === "characterData") {
            visibleMutation = true;
            break;
          }
        }
      } catch {
        mutationRecordCount = 0;
      }
      if (!visibleMutation) {
        failed.push("dom_mutation");
        // mutationRecordCount > 0 here means observer recorded SOMETHING that
        // didn't pass the visibility filter (none today after the spec-aligned
        // expansion above, but kept for the "added in a future tightening"
        // path). Used to downgrade confidence to "medium".
        if (mutationRecordCount > 0) subPerceptual = true;
      }
      // 4. network — count resource entries started after click
      let networkActive = false;
      try {
        const entries = performance.getEntriesByType("resource");
        // Fast-path: only inspect entries beyond the perfBefore snapshot.
        for (let i = entry.perfBefore; i < entries.length; i++) {
          const e = entries[i];
          if (e && e.startTime > entry.perfStart - 1) {
            networkActive = true;
            break;
          }
        }
      } catch {
        // Performance API absent — treat as no network (safer to over-report
        // dead than under-report).
      }
      if (!networkActive) failed.push("network");
      // 5. focus change
      if (!focusChangedMeaningfully(entry.focusAtClick, document.activeElement)) {
        failed.push("focus_change");
      }
      // 6. media / scroll
      const scrollNow = readScrollY();
      const mediaNow = readMediaState();
      const scrolled = Math.abs(scrollNow - entry.scrollAtClick) > SCROLL_THRESHOLD_PX;
      const mediaChanged = mediaNow !== entry.mediaStateAtClick;
      if (!scrolled && !mediaChanged) failed.push("media_scroll");

      // Disconnect observers regardless of outcome (memory-leak safety).
      disconnectEntryObservers(entry);

      if (failed.length < 6) {
        // At least one response signal — NOT dead. Drop entry so subsequent
        // clicks on the same key get a fresh verdict (in case the element
        // was just slow-loading and is now responsive).
        map.delete(key);
        return;
      }

      // All six checks failed → confirmed dead. Lock the verdict info, arm
      // the trailing rage debounce, and wait for clicks-to-emit.
      entry.checksFailed = failed;
      entry.confidence = subPerceptual ? "medium" : "high";
      entry.state = "confirmed-dead";
      scheduleRageFlush(key, entry);
    }

    function startVerdict(key: string, entry: PerViewEntry): void {
      let mutationObs: MutationObserver | null = null;
      try {
        // noop callback — we drain via takeRecords() at verdict time.
        mutationObs = new MutationObserver(() => undefined);
        mutationObs.observe(document.body, {
          childList: true,
          attributes: true,
          characterData: true,
          subtree: true,
        });
      } catch {
        mutationObs = null;
      }
      entry.mutationObs = mutationObs;
      entry.verdictTimer = window.setTimeout(() => {
        entry.verdictTimer = null;
        runVerdict(key, entry);
      }, DETECTION_WINDOW_MS);
    }

    function onClick(event: MouseEvent): void {
      try {
        // Left-click only — middle/right are routed elsewhere (scroll, context
        // menu) and aren't part of the dead-click contract.
        if (event.button !== 0) return;

        const target = event.target;
        if (!(target instanceof Element)) return;

        // Eligibility: must be a click on (or within 3 ancestors of) an
        // interactive-looking element.
        const interactive = findInteractiveAncestor(target);
        if (!interactive) return;

        // Exclusions: disabled / aria-disabled / MUI disabled anywhere on
        // the chain disqualifies. (Note: most `.btn:disabled` clicks never
        // reach this listener at all because `pointer-events: none` removes
        // them from the hit-test tree — this check is the safety net for
        // app-specific disabled patterns that don't use pointer-events.)
        if (isExcludedElement(interactive)) return;

        // App-is-busy gate — the click was made against a legitimately-busy
        // app and "no response within 800ms" is not the user's fault. Read
        // both the React-mirrored refs AND the localStorage-backed streaming
        // flag (cheap, no context dependency).
        if (isBusyRef.current) return;
        if (isStreamingActive()) return;

        // Hard cap — drop without even verdicting. Counter is a sessionStorage
        // integer, persists across navigations within the same tab.
        if (readSessionCap() >= SESSION_CAP_MAX) return;

        const selector = sanitizeSelector(interactive);
        const viewKey = currentViewKeyRef.current;
        const key = `${selector}|${viewKey}`;

        const existing = map.get(key);
        if (existing) {
          existing.count++;
          if (existing.state === "confirmed-dead") {
            // Rapid repeat on a known-dead element → reset the trailing
            // debounce so all repeats collapse into one emitted event.
            scheduleRageFlush(key, existing);
          }
          // If state is "pending", the running 800ms verdict will eventually
          // emit using the bumped count. If state is "emitted", we've already
          // fired the event for this key in this view — count keeps tracking
          // locally but no further events fire (one event per key per view).
          return;
        }

        // ──── First click on this key — open a verdict ─────────────────
        let entries: PerformanceEntryList = [];
        try {
          entries = performance.getEntriesByType("resource");
        } catch {
          entries = [];
        }
        const entry: PerViewEntry = {
          count: 1,
          state: "pending",
          selector,
          elementRole: getElementRole(interactive),
          elementText: getElementText(interactive),
          x: event.clientX,
          y: event.clientY,
          route: pathnameRef.current,
          viewKey,
          url: window.location.href,
          screenAtClick: screenRef.current || "",
          focusAtClick: document.activeElement,
          scrollAtClick: readScrollY(),
          mediaStateAtClick: readMediaState(),
          perfStart: performance.now(),
          perfBefore: entries.length,
          mutationObs: null,
          verdictTimer: null,
          rageTimer: null,
          checksFailed: [],
          confidence: "high",
        };
        map.set(key, entry);
        startVerdict(key, entry);
      } catch {
        // Defensive: any thrown error inside this handler is swallowed so a
        // bug in dead-click telemetry can never break user interactions.
      }
    }

    document.addEventListener("click", onClick, { capture: true, passive: true });

    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      // Drain and dispose every in-flight entry so timers/observers don't
      // leak past unmount (App.tsx is the root, so unmount = full teardown).
      map.forEach((entry) => {
        try {
          entry.mutationObs?.disconnect();
        } catch {
          // ignore
        }
        if (entry.verdictTimer !== null) window.clearTimeout(entry.verdictTimer);
        if (entry.rageTimer !== null) window.clearTimeout(entry.rageTimer);
      });
      map.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
